'use client';

import React, {
  createContext,
  useCallback,
  useContext,
  useRef,
  useState
} from 'react';

interface LoadingBarContextType {
  startLoading: () => void;
  completeLoading: () => void;
  loading: boolean;
  progress: number;
}

const LoadingBarContext = createContext<LoadingBarContextType | undefined>(
  undefined
);

export const LoadingBarProvider: React.FC<{ children: React.ReactNode }> = ({
  children
}) => {
  const [loading, setLoading] = useState(false);
  const [progress, setProgress] = useState(0);
  const progressTimeout = useRef<NodeJS.Timeout | null>(null);
  const completeTimeout = useRef<NodeJS.Timeout | null>(null);

  const startLoading = useCallback(() => {
    if (progressTimeout.current) clearTimeout(progressTimeout.current);
    if (completeTimeout.current) clearTimeout(completeTimeout.current);
    setLoading(true);
    setProgress(0);

    const simulateProgress = () => {
      setProgress((prev) => {
        if (prev >= 90) return prev;
        return prev + (90 - prev) * 0.1;
      });
      progressTimeout.current = setTimeout(simulateProgress, 100);
    };
    simulateProgress();
  }, []);

  const completeLoading = useCallback(() => {
    if (progressTimeout.current) clearTimeout(progressTimeout.current);
    setProgress(100);
    completeTimeout.current = setTimeout(() => {
      setLoading(false);
      setProgress(0);
    }, 200);
  }, []);

  return (
    <LoadingBarContext.Provider
      value={{ startLoading, completeLoading, loading, progress }}
    >
      {children}
    </LoadingBarContext.Provider>
  );
};

export function useLoadingBar() {
  const context = useContext(LoadingBarContext);
  if (!context) {
    throw new Error('useLoadingBar must be used within a LoadingBarProvider');
  }
  return context;
}
